import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import repairmanService from '../../services/repairmanService';
import './RepairmanList.scss';

const RepairmanList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [repairmen, setRepairmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRepairman, setSelectedRepairman] = useState(null);

  useEffect(() => {
    // Mark this page as not requiring login to prevent auth redirects
    localStorage.setItem('skipLoginCheck', 'true');
    sessionStorage.setItem('skipLoginCheck', 'true');

    fetchRepairmen();

    // Cleanup on unmount
    return () => {
      localStorage.removeItem('skipLoginCheck');
      sessionStorage.removeItem('skipLoginCheck');
    };
  }, []);

  const fetchRepairmen = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await repairmanService.getAllRepairmen();
      
      if (result.success) {
        setRepairmen(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch repairmen');
      console.error('Error fetching repairmen:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRepairmanSelect = (repairman) => {
    setSelectedRepairman(repairman);
  };

  const handleCallRepairman = (phoneNumber) => {
    if (phoneNumber) {
      window.open(`tel:${phoneNumber.replace(/\s/g, '')}`, '_self');
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const getDisplayName = (repairman) => {
    return `${repairman.name || ''} ${repairman.surname || ''}`.trim();
  };

  const getTechnicalAreaName = (technicalArea) => {
    if (!technicalArea) return t('repairmanList.noSpecialty');
    
    if (typeof technicalArea === 'object' && technicalArea.name) {
      return technicalArea.name[i18n.language] || technicalArea.name.az || technicalArea.name.en || technicalArea.name.ru || technicalArea.name;
    }
    
    return technicalArea;
  };

  const renderRepairmanCard = (repairman) => (
    <div 
      key={repairman._id} 
      className={`repairman-card ${selectedRepairman?._id === repairman._id ? 'selected' : ''}`}
      onClick={() => handleRepairmanSelect(repairman)}
    >
      <div className="repairman-avatar">
        {repairman.image ? (
          <img 
            src={repairman.image.path || repairman.image} 
            alt={getDisplayName(repairman)}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="avatar-fallback">
          <i className="bi bi-person-circle"></i>
        </div>
      </div>
      
      <div className="repairman-info">
        <div className="repairman-name">
          {getDisplayName(repairman)}
        </div>
        <div className="repairman-specialty">
          {getTechnicalAreaName(repairman.technicalArea)}
        </div>
        <div className="repairman-status">
          <span className={`status-badge ${repairman.status ? 'available' : 'unavailable'}`}>
            {repairman.status ? t('repairmanList.available') : t('repairmanList.unavailable')}
          </span>
        </div>
      </div>
      
      <div className="repairman-actions">
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            handleCallRepairman(repairman.phoneNumber);
          }}
          disabled={!repairman.status}
        >
          <i className="bi bi-telephone-fill me-1"></i>
          {t('repairmanList.call')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="repairman-list-page">
      <Header
        title={t('repairmanList.title')}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={handleBackClick}
      />

      <div className="container repairman-list-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
            <p className="mt-2">{t('repairmanList.loadingRepairmen')}</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="alert alert-danger">
              <h5>{t('common.error')}</h5>
              <p>{error}</p>
              <button 
                className="btn btn-outline-danger"
                onClick={fetchRepairmen}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                {t('common.retry')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="repairman-summary">
              <h6>{t('repairmanList.summary')}</h6>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">{t('repairmanList.totalRepairmen')}:</span>
                  <span className="stat-value">{repairmen.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{t('repairmanList.availableRepairmen')}:</span>
                  <span className="stat-value">
                    {repairmen.filter(r => r.status).length}
                  </span>
                </div>
              </div>
            </div>

            {/* Repairman List */}
            <div className="repairman-list">
              {repairmen.length === 0 ? (
                <div className="no-repairmen">
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('repairmanList.noRepairmen')}
                  </div>
                </div>
              ) : (
                <>
                  <h6 className="section-title">
                    {t('repairmanList.availableRepairmen')} ({repairmen.filter(r => r.status).length})
                  </h6>
                  
                  {/* Available Repairmen */}
                  <div className="repairmen-grid">
                    {repairmen
                      .filter(repairman => repairman.status)
                      .map(renderRepairmanCard)}
                  </div>

                  {/* Unavailable Repairmen */}
                  {repairmen.filter(r => !r.status).length > 0 && (
                    <>
                      <h6 className="section-title mt-4">
                        {t('repairmanList.unavailableRepairmen')} ({repairmen.filter(r => !r.status).length})
                      </h6>
                      
                      <div className="repairmen-grid unavailable-section">
                        {repairmen
                          .filter(repairman => !repairman.status)
                          .map(renderRepairmanCard)}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Selected Repairman Details */}
            {selectedRepairman && (
              <div className="selected-repairman-details">
                <h6>{t('repairmanList.repairmanDetails')}</h6>
                <div className="details-card">
                  <div className="detail-row">
                    <span className="label">{t('repairmanList.name')}:</span>
                    <span className="value">{getDisplayName(selectedRepairman)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('repairmanList.specialty')}:</span>
                    <span className="value">{getTechnicalAreaName(selectedRepairman.technicalArea)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('repairmanList.phone')}:</span>
                    <span className="value">
                      <a href={`tel:${selectedRepairman.phoneNumber?.replace(/\s/g, '')}`}>
                        {selectedRepairman.phoneNumber}
                      </a>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('repairmanList.status')}:</span>
                    <span className={`value status ${selectedRepairman.status ? 'available' : 'unavailable'}`}>
                      {selectedRepairman.status ? t('repairmanList.available') : t('repairmanList.unavailable')}
                    </span>
                  </div>
                  {selectedRepairman.teams && selectedRepairman.teams.length > 0 && (
                    <div className="detail-row">
                      <span className="label">{t('repairmanList.teams')}:</span>
                      <span className="value">{selectedRepairman.teams.length} {t('repairmanList.teamsCount')}</span>
                    </div>
                  )}
                </div>
                
                <div className="action-buttons">
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleCallRepairman(selectedRepairman.phoneNumber)}
                    disabled={!selectedRepairman.status}
                  >
                    <i className="bi bi-telephone-fill me-2"></i>
                    {t('repairmanList.contactRepairman')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RepairmanList; 